/**
 * ATL-1304 — the two P&L lines that are DERIVED from a daily stat rather than read off it:
 * «Разница в закрытиях смен» (`moneyDifferenceAmount`) and «Комиссия банка» (`bankPercent`).
 *
 * Extracted from `ProfitStats.js` unchanged in behaviour, for two reasons.
 *
 * 1. It can now be tested against the writer it mirrors — `dailyStatIncrementsForSales`
 *    (`global/Firebase/sales.ts`), ported to the backend as
 *    `daily-stats-drift.util.ts#saleStatIncrements`. Both P&L lines are subtracted from
 *    `opexProfit` and therefore from `profit`, and until this file existed the only way to
 *    exercise the arithmetic was to render the screen.
 * 2. The commission had a latent `NaN` (see `bankPercentFor`), which no gate could see because
 *    `currencyFormatter.format(NaN)` renders a value rather than an error.
 *
 * THE TWO ERAS. The Firestore `dailyStats` document has two shapes and `shiftOpened == null` is
 * the only thing that tells them apart. A document written before per-wallet shift accounting
 * carries no `shiftOpened`, three fixed difference fields and an accumulated `bankPercent`;
 * every document since carries `shiftOpened` plus a flat bag keyed by payment method and by
 * wallet, from which both figures are recomputed. A Supabase row is always the second shape —
 * `transformDailyStat` emits `shiftOpened` as a boolean, and `false` is not `null` — which is
 * why `global/Firebase/stats.ts` must carry `shiftOpened` through its roll-up VERBATIM
 * (`NON_NUMERIC_STAT_FIELDS`): summing it across three stores would answer that probe with `3`.
 */

/** The subset of a payment method this arithmetic reads. */
export interface ProfitStatsPaymentMethod {
  id?: string
  /** Legacy Firestore spelling. The backend emits both; `payment-methods.service.ts` :88. */
  comission?: number | null
  commission?: number | null
  destinationWalletForStore?: Record<string, { id?: string } | undefined> | null
}

export interface DailyShiftAmounts {
  moneyDifferenceAmount: number
  bankPercent: number
}

/**
 * Read a stat field as money. An absent key, `null`, or anything that is not a finite number
 * reads as 0 — never as `NaN`, which `|| 0` at a call site would later launder into a
 * believable figure (ATL-1062).
 */
const amount = (value: unknown): number => {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

/**
 * Where the store's cash takings are booked. The writer keys cash by the payment method's
 * DESTINATION WALLET rather than by the method id, because the drawer is reconciled per wallet
 * and two stores' cash lands in two different wallets. `<store>-cash` is the conventional id the
 * app itself falls back to when a method names no wallet (`firebase.ts` :3091).
 */
export const resolveCashWalletId = (
  paymentMethods: readonly ProfitStatsPaymentMethod[] | null | undefined,
  store: string
): string => {
  for (const pm of paymentMethods ?? []) {
    if (pm?.id === 'cash') {
      return pm.destinationWalletForStore?.[store]?.id || store + '-cash'
    }
  }
  return store + '-cash'
}

/**
 * The key a payment method's figures are filed under in the flat bag: the destination wallet
 * for cash, the method's own id for everything else.
 */
const statKeyPrefix = (
  paymentMethod: ProfitStatsPaymentMethod,
  paymentMethods: readonly ProfitStatsPaymentMethod[] | null | undefined,
  store: string
): string =>
  paymentMethod.id === 'cash' ? resolveCashWalletId(paymentMethods, store) : String(paymentMethod.id)

/**
 * The commission this method charges, as a fraction.
 *
 * `comission / 100` on a method that has none is `NaN`, and NaN is contagious: ONE such method
 * turned the whole «Комиссия банка» row — and `opexProfit`, and `profit` — into NaN, which the
 * currency formatter renders as a value. A method with no commission configured charges none.
 */
const bankPercentFor = (paymentMethod: ProfitStatsPaymentMethod): number =>
  amount(paymentMethod.comission ?? paymentMethod.commission) / 100

/**
 * The day's cash-difference and bank-commission contributions, for ONE daily stat.
 *
 * @param stat            one `dailyStats` document / `/reports/daily-stats` row
 * @param paymentMethods  `constants.paymentMethods.data`
 */
export const dailyShiftAmounts = (
  stat: Record<string, unknown> | null | undefined,
  paymentMethods: readonly ProfitStatsPaymentMethod[] | null | undefined
): DailyShiftAmounts => {
  const row = (stat ?? {}) as Record<string, unknown>

  if (row.shiftOpened == null) {
    // Pre-shift-accounting document: the two figures are stored, not derived.
    return {
      moneyDifferenceAmount:
        amount(row.cashDifferenceAmount) +
        amount(row.cardDifferenceAmount) +
        amount(row.kaspiDifferenceAmount),
      bankPercent: amount(row.bankPercent),
    }
  }

  const store = String(row.store ?? '')
  let moneyDifferenceAmount = 0
  let bankPercent = 0

  for (const paymentMethod of paymentMethods ?? []) {
    const key = statKeyPrefix(paymentMethod, paymentMethods, store)
    // Counted minus expected. BOTH halves must be read: a day that was never closed has
    // neither, and `0 - 0` is the honest answer, while reading only `SystemEndAmount` would
    // book the whole day's takings as a cash shortfall.
    moneyDifferenceAmount += amount(row[key + 'EndAmount']) - amount(row[key + 'SystemEndAmount'])
    bankPercent += amount(row[key + 'SalesAmount']) * bankPercentFor(paymentMethod)
  }

  return { moneyDifferenceAmount, bankPercent }
}
